import axios from 'axios';
import Bowser from 'bowser';
import { get } from 'lodash';
import { CONTENT_EVENTS } from '@upsolve/shared';

import * as contentGroupsEnums from './contentGroups';
import contentGroupMap from './contentGroupMap';

interface IPageViewEvent {
  eventType: string;
  deviceId: string;
  eventProperties: {
    browser?: {
      name: string;
      version: string;
    };
    canonicalUrl?: string;
    contentCreationMethod?: string;
    contentGroup?: string;
    contentLocalized?: boolean;
    jsonldTypes?: string[];
    os?: {
      name: string;
      version: string;
      versionName: string;
    }
    pageComponentName?: string;
    pageComponentVersion?: number;
    path?: string;
    platform?: {
      type: string;
      vendor: string;
    };
    referrer?: string;
    screenHeight?: number;
    screenWidth?: number;
    searchTermTarget?: string;
    title?: string;
    url?: string;
    wordCount?: number;
  };
}

function setIntervalX(callback, delay, repetitions) {
  let x = 0;
  let intervalID = window.setInterval(() => {
    callback();
    if (++x === repetitions) {
      window.clearInterval(intervalID);
    }
  }, delay);
}

// https://segment.com/docs/destinations/google-analytics/#content-groupings
export default async function trackPageView({
  canonicalUrl,
  contentCreationMethod,
  contentGroup,
  contentLocalized,
  jsonldTypes,
  pageComponentName,
  pageComponentVersion,
  searchTermTarget,
  title,
  wordCount,
}: IPageViewEvent["eventProperties"]) {
  if (pageComponentName == null) throw new Error('Missing Page Component Name');
  if (pageComponentVersion == null) throw new Error('Missing Page Component Version');

  const anonymousId = get(window, 'analytics.user') != null
    ? window.analytics.user().anonymousId()
    : null;

  // FRONTDOOR TRACKING
  try {
    const browserProps = Bowser.parse(window.navigator.userAgent);
    const trackBody: IPageViewEvent = {
      eventType: CONTENT_EVENTS.PAGE_VIEW,
      deviceId: anonymousId,
      eventProperties: {
        browser: browserProps.browser,
        canonicalUrl,
        contentCreationMethod,
        contentGroup,
        contentLocalized,
        jsonldTypes,
        os: browserProps.os,
        pageComponentName,
        pageComponentVersion,
        path: window.location.pathname,
        platform: browserProps.platform,
        referrer: document.referrer,
        screenHeight: window.innerHeight,
        screenWidth: window.innerWidth,
        searchTermTarget,
        title,
        url: window.location.href,
        wordCount,
      }
    };
    await axios.post(`${UPSOLVE_API_URL}/v1/tracking/frontdoor`, trackBody);
  } catch (error) {
    console.warn(error);
  }

  // SEGMENT USER PROPS
  let trackingCallMade = false;
  return setIntervalX(async () => {
    if (trackingCallMade === true) return;
    if (window.analytics == null) return;
    if (window.analytics.user == null) return;
    try {
      if (contentGroup == null) {
        console.warn('No content group specified.');
      }
      if (!Object.values(contentGroupsEnums).includes(contentGroup)) {
        console.warn(`Not a content grouping: ${contentGroup}`)
      }

      // Page Event
      const pageProps = {
        path: location.pathname,
        referrer: document.referrer,
        title: document.title,
        url: location.href,
      };
      // Traits Updating
      const thisInteraction = {
        interactionType: 'pageView',
        contentGroup,
        interactionAt: new Date(),
        path: location.pathname,
        searchTermTarget,
      };
      const userTraits = await window.analytics.user().traits();
      const newTraits = {
        allInteractions: get(userTraits, 'allInteractions', [])
          .concat(thisInteraction),
        numInteractions: get(userTraits, 'numInteractions', 0) + 1,
        lastInteraction: thisInteraction,
      };
      // If no firstInteraction was set, this is a new user and should set
      if (userTraits.firstInteraction == null) {
        newTraits.firstInteraction = thisInteraction;
      }

      // Update!
      await window.analytics.page(pageProps);
      await window.analytics.identify(newTraits);
      trackingCallMade = true;
    } catch (e) {
      console.error(e);
    }
  }, 600, 4);
}
